import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../auth/AuthContext';
import { BUSINESSES } from '../domain/businesses';

/**
 * App shell. Mirrors the desktop sidebar (frmMain) including its collapsible
 * groups, but the whole thing slides away on small screens — the point of the
 * port is that this works on a phone.
 *
 * The groups behave like frmMain's: one open at a time, and opening one closes
 * the others (btnShpenzimet_Click and its siblings each start the other two
 * containers' collapse animations). The group holding the current route opens
 * itself, so a page reload never leaves the active link hidden.
 *
 * Navigation visibility follows the role, exactly as the desktop app chose
 * between frmMain and frmMainManager (frmLogin.cs:75). The server enforces the
 * same rule per endpoint; hiding links is convenience, not security.
 */

interface NavGroup {
  /** Stable identity. Not shown; labels are not unique and some are empty. */
  id: string;
  /** Heading text. Empty renders the items with no heading at all. */
  label: string;
  /** Roles allowed to see this group. Empty means everyone. */
  roles?: string[];
  /** A group with no label renders as plain links, with nothing to collapse. */
  collapsible?: boolean;
  items: { label: string; to: string }[];
}

/**
 * Owner sees everything frmMain offers.
 *
 * Starbar sees exactly one screen. frmMainManager (frmMainManager.cs:45-55) has
 * a single button and loads FitimetStarbar on open — that role is a Starbar
 * revenue view and nothing else. Giving it the expense screens would be a
 * privilege escalation the desktop app never had.
 */
const NAV: NavGroup[] = [
  {
    id: 'dashboard',
    label: '',
    roles: [ROLES.OWNER],
    items: [{ label: 'Paneli i kontrollit', to: '/' }],
  },
  {
    id: 'aktuale',
    label: 'Shpenzimet aktuale',
    roles: [ROLES.OWNER],
    collapsible: true,
    items: [
      ...BUSINESSES.map((b) => ({
        label: b.name,
        to: `/shpenzimet/aktuale/${b.slug}`,
      })),
      { label: 'Rrogat', to: '/rrogat' },
      { label: 'Pagesat', to: '/pagesat/aktuale' },
    ],
  },
  {
    id: 'planifikuara',
    label: 'Shpenzimet e planifikuara',
    roles: [ROLES.OWNER],
    collapsible: true,
    items: [
      ...BUSINESSES.map((b) => ({
        label: b.name,
        to: `/shpenzimet/planifikuara/${b.slug}`,
      })),
      { label: 'Rrogat', to: '/rrogat/planifikuara' },
      { label: 'Pagesat', to: '/pagesat/planifikuara' },
    ],
  },
  {
    id: 'devijimi',
    label: 'Devijimi',
    roles: [ROLES.OWNER],
    collapsible: true,
    items: [
      ...BUSINESSES.map((b) => ({
        label: b.name,
        to: `/shpenzimet/devijimi/${b.slug}`,
      })),
      { label: 'Rrogat', to: '/rrogat/devijimi' },
    ],
  },
  {
    /**
     * The five tabs frmFitimet offers (frmFitimet.cs:104-146): the three
     * businesses that have revenue, the Stargroup comparison chart, and the
     * report. "Stargroup" is the comparison view — Stargroup has no revenue
     * rows of its own, its figure is the sum of the other three.
     */
    id: 'fitimi',
    label: 'Fitimi',
    roles: [ROLES.OWNER],
    collapsible: true,
    items: [
      { label: 'Stargym', to: '/fitimet/stargym' },
      { label: 'Missfit', to: '/fitimet/missfit' },
      { label: 'Starbar', to: '/fitimet/starbar' },
      { label: 'Stargroup', to: '/fitimet/krahasimi' },
      { label: 'Raportet', to: '/fitimet/raportet' },
    ],
  },
  {
    // Not collapsible: unlike the four above, these are unrelated one-off
    // screens rather than one screen per business, so there is no family to
    // fold away. frmMain lists them as plain buttons for the same reason.
    id: 'tjera',
    label: '',
    roles: [ROLES.OWNER],
    items: [
      { label: 'Klientat — Stargym', to: '/klientat/stargym' },
      { label: 'Klientat — Missfit', to: '/klientat/missfit' },
      { label: 'Stafi', to: '/stafi' },
      { label: 'Borxhet', to: '/borxhet' },
      { label: 'Restorant', to: '/restorant' },
      { label: 'Kalendari', to: '/kalendar' },
      { label: 'Nënkategoritë', to: '/nenkategorit/shpenzimet' },
      { label: 'Shpenzimet automatike', to: '/shpenzimet/automatike' },
      { label: 'SOP', to: '/sop' },
    ],
  },
  {
    id: 'starbar',
    label: '',
    roles: [ROLES.STARBAR],
    items: [{ label: 'Fitimet — Starbar', to: '/fitimet/starbar' }],
  },
];

/** Where each role lands after login, mirroring OpenFormBasedOnRole. */
export function homeRouteForRole(role: string | undefined): string {
  return role === ROLES.STARBAR ? '/fitimet/starbar' : '/';
}

/**
 * The group a path belongs to, or null when no menu item covers it.
 *
 * Longest match wins: /rrogat, /rrogat/planifikuara and /rrogat/devijimi sit in
 * three different groups, and a plain prefix test would pick the wrong one.
 */
function groupForPath(groups: NavGroup[], pathname: string): NavGroup | null {
  let best: { group: NavGroup; length: number } | null = null;

  for (const group of groups) {
    for (const item of group.items) {
      const matches =
        pathname === item.to || (item.to !== '/' && pathname.startsWith(`${item.to}/`));
      if (matches && (!best || item.to.length > best.length)) {
        best = { group, length: item.to.length };
      }
    }
  }

  return best?.group ?? null;
}

/**
 * Which group should be open once the route is `pathname`.
 *
 * `undefined` means leave the menu alone — the route belongs to no menu item,
 * as with the report screens reached by a link inside another page.
 *
 * Landing on an always-open screen closes whatever was expanded, which is what
 * frmMain does: its flat buttons all call closeForms(), collapsing the open
 * container.
 */
function openGroupForPath(
  groups: NavGroup[],
  pathname: string,
): string | null | undefined {
  const match = groupForPath(groups, pathname);
  if (!match) return undefined;
  return match.collapsible ? match.id : null;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const groups = NAV.filter((g) => !g.roles || (user && g.roles.includes(user.role)));

  // One open at a time, as frmMain's containers behave.
  const [openGroup, setOpenGroup] = useState<string | null>(
    () => openGroupForPath(groups, pathname) ?? null,
  );

  // Follow the route: navigating into a collapsed group opens it, and going to
  // one of the always-visible screens collapses whatever was open.
  useEffect(() => {
    const next = openGroupForPath(groups, pathname);
    if (next !== undefined) setOpenGroup(next);
    // groups is derived from a module constant and the user's role; rebuilding
    // the effect on every render would fight the user's own clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user?.role]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-full">
      {/* Backdrop, mobile only */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Mbyll menunë"
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-72 shrink-0 overflow-y-auto bg-ink text-slate-200 transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="px-5 py-5 text-lg font-semibold tracking-wide text-white">
          Star Group ERP
        </div>

        <nav className="px-2 pb-8">
          {groups.map((group) => {
            const expanded = !group.collapsible || openGroup === group.id;

            return (
              <div key={group.id} className="mb-2">
                {group.collapsible ? (
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() =>
                      setOpenGroup((current) =>
                        current === group.id ? null : group.id,
                      )
                    }
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      expanded
                        ? 'text-white'
                        : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }`}
                  >
                    <span className="flex-1 text-left">{group.label}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                      className={`shrink-0 transition-transform duration-200 ${
                        expanded ? 'rotate-90' : ''
                      }`}
                    >
                      <path d="M7 4l6 6-6 6z" />
                    </svg>
                  </button>
                ) : (
                  group.label && (
                    <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {group.label}
                    </div>
                  )
                )}

                {expanded &&
                  group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `block rounded-lg py-2 text-sm transition-colors ${
                          group.collapsible ? 'pl-6 pr-3' : 'px-3'
                        } ${
                          isActive
                            ? 'bg-brand-500 font-medium text-white'
                            : 'text-slate-300 hover:bg-white/10'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <button
            type="button"
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Hap menunë"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M3 5h14v2H3zM3 9h14v2H3zM3 13h14v2H3z" />
            </svg>
          </button>

          <div className="ml-auto flex items-center gap-3 text-sm">
            <span className="text-muted">
              {user?.username}
              <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                {user?.role}
              </span>
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
            >
              Shkyçu
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
